"use client"

import { useEffect, useId } from "react"
import { useLatestValue } from "./useLatestValue"

/*
 * Why this solution?
 *
 * It's easy enough to add contextual keydown listeners on components like <Modal>,
 * <Select>, and <Popover>, where keydown presses can be captured within the container,
 * and propagation can be stopped from leaking into other listeners.
 *
 * The challenge arises when something that closes with `esc` is not literally
 * focused. A good example of this is info-only tooltips - imagine being focused in an input,
 * then hovering a nearby tooltip, and having your literal focus unexpectedly shifting around.
 *
 * The above case still isn't impossible to account for, but the clever tricks required quickly
 * become unideal, and a simple, single-source of truth becomes more desirable.
 *
 */

type Handler = {
  id: string
  callback: React.MutableRefObject<() => void>
}

let handlers: Handler[] = []
let listenerBound = false

const handleKeyDown = (evt: KeyboardEvent) => {
  if (evt.key === "Escape") {
    // Attempt to call the first handler in the stack, if it exists
    const [firstHandler] = handlers

    if (firstHandler) {
      evt.preventDefault()
      firstHandler.callback.current?.()
    }
  }
}

const managerListener = () => {
  if (handlers.length > 0 && !listenerBound) {
    document.body.addEventListener("keydown", handleKeyDown)
    listenerBound = true
  } else if (handlers.length === 0 && listenerBound) {
    document.body.removeEventListener("keydown", handleKeyDown)
    listenerBound = false
  }
}

const registerHandler = (handler: Handler) => {
  handlers.unshift(handler)
  managerListener()
}

const unregisterHandler = ({ id }: Handler) => {
  handlers = handlers.filter((h) => h.id !== id)
  managerListener()
}

export const useEscCloseStack = (listening: boolean, cb: () => void) => {
  const id = useId()
  const latestCallback = useLatestValue(cb)

  useEffect(() => {
    if (!listening) {
      return
    }

    const handler = { id, callback: latestCallback }
    registerHandler(handler)

    return () => unregisterHandler(handler)
  }, [id, listening, latestCallback])
}
